/**
 * The steps a store cross-grade runs through, written down once.
 *
 * Four paths ask a store to REPLACE a subscription the buyer already holds with a
 * different product: `AppleSubscriptionStrategy.switchPlan` and
 * `GooglePlaySubscriptionStrategy.switchPlan` for a subscriber changing their
 * personal plan (`subscription-strategy.ts`), and `changeTierAppleAsync` and
 * `changeTierGoogleAsync` for a coach changing their coaching tier
 * (`coach-tier.service.ts`). They call different validate endpoints and refresh the
 * session in different ways, but the ORDER of what they do, and the one decision
 * they make about the buyer's money, is the same. This file holds that order and that
 * decision; each caller supplies only the parts that genuinely differ.
 *
 * WHY THIS FILE EXISTS. The sequence used to be written out four times, and the rule
 * below had to be added to each copy by hand: `df-1163c1e120a1` added it to the two
 * plan-switch paths, and `df-7cdfc531d3ca` had to come back afterwards for the two
 * coach-tier paths, which had been missed. `df-1cc85c54e6fc` folded them into this
 * one function so the next change to the rule is one edit, with a test beside it.
 *
 * THE ONE RULE IT HOLDS. `storeChargedTheBuyer` becomes true at exactly one point:
 * the moment the store hands us a purchase token. From there the store has made the
 * change and, on an upgrade, taken the money, so a throw from then on means we got no
 * answer about the change from anywhere — the same "we could not check" situation the
 * server reports with its own `store_unavailable` code. So it gets the same flag,
 * `confirmationFailedAfterPurchase: true`, and nothing shown to that person may say
 * their change failed (#4651). Set any earlier, the flag would promise a change to
 * somebody whose plan never moved.
 *
 * A throw from BEFORE that point is deliberately not answered here at all: it is
 * rethrown, so it keeps reaching each caller's own outer catch
 * (`onSwitchPlanClick` for the personal paths, `changeTierAsync` for the coach
 * paths), which reports the whole error object. On iOS the native side no longer
 * rejects a purchase StoreKit has already charged just because the device could not
 * verify its receipt: it hands the token over with `unverifiedOnDevice: true`, and
 * the change is confirmed with the server like any other.
 *
 * THE SAME POINT IS WHERE A PATH GETS TO WRITE THE CHANGE DOWN (#5611).
 * `rememberUnconfirmedPurchase` is called there, and `onServerAnswered` once the
 * confirmation resolves — the same pair, at the same two points, as in
 * `store-purchase-flow.ts`. A coach's Google tier change needs it most: Google issues
 * a NEW purchase token, so if our confirmation throws, the store notification for
 * that token matches no account and nothing else ever tells our server. The coach
 * paths write the confirmation request down and `CoachTierService` re-sends it at the
 * next app open. The personal paths recover on their own and pass empty bodies.
 *
 * HOW THIS DIFFERS FROM `store-purchase-flow.ts`, AND WHY THEY ARE NOT ONE FUNCTION.
 * A first purchase never throws to its caller and handles an already-owned answer.
 * A cross-grade does neither: it rethrows pre-token failures, and has no
 * already-owned branch (an `alreadyOwned` answer with no token falls through to the
 * missing-token refusal, exactly as it always has). It is a plain function with no
 * Angular or Capacitor import for the same reason that file is, so
 * `store-cross-grade-flow.test.ts` can run it under mocha with plain fakes.
 */

import { describeError } from '@cf-shared/error-describe';
import {
  ConfirmedStorePurchaseResponse,
  NO_PURCHASE_TOKEN_ERROR,
  StorePurchaseResponse,
  StoreValidationResponse,
} from './store-purchase-flow';

/** What one cross-grade path answers with. Structural, so both
 *  `SwitchPlanResult` and `CoachTierActionResult` accept it without a cast. */
export interface StoreCrossGradeOutcome {
  success: boolean;
  canceled?: boolean;
  error?: string;
  linkedEmail?: string;
  scheduled?: boolean;
  confirmationFailedAfterPurchase?: boolean;
}

/** The parts that differ between one cross-grade path and another.
 *
 *  `TValidation` is inferred from `validateAsync` alone — the `NoInfer` on the other
 *  members stops a looser failure builder from widening it. */
export interface StoreCrossGradeFlow<TValidation extends StoreValidationResponse> {
  /** the hashed user id the store ties the purchase to. Only the Google paths
   *  supply it; when absent, no account id is fetched. Called inside the guarded
   *  section, so a throw here is a pre-token throw and is rethrown. */
  getAccountIdAsync?(): Promise<string | undefined>;

  /** ask the store to replace the current subscription with the target product. */
  runStoreCallAsync(accountId: string | undefined): Promise<StorePurchaseResponse>;

  /** the store short-circuited to a finished historical transaction instead of
   *  prompting. Only the personal Apple path reports it; absent means ignored. */
  reportStaleTransaction?(response: StorePurchaseResponse): void;

  /** the store made the change, but the device could not verify the receipt (iOS).
   *  Reported, then confirmed with our server exactly like any other change.
   *  Required so no path can drop the report by leaving it out. */
  reportUnverifiedOnDevice(response: StorePurchaseResponse): void;

  /** the store has just made the change, and nobody but this device knows it yet.
   *
   *  Called at exactly one point — the moment the purchase token arrives, before
   *  our server is told anything — so a path with no other way back can write the
   *  change down and re-send it later. Nothing here may throw: a failure to
   *  remember must not stop the confirmation that follows.
   *
   *  Required rather than optional, so no path inherits nothing by accident. A
   *  path with a recovery of its own says so with an empty body and a comment. */
  rememberUnconfirmedPurchase(
    response: ConfirmedStorePurchaseResponse,
    accountId: string | undefined,
  ): void;

  /** our server has answered — granted, refused, or said it could not check.
   *
   *  Called once `validateAsync` RESOLVES, whatever it resolved with, and never
   *  when it throws: a throw is the case the record above exists for. Which
   *  answers are worth keeping the record for is the caller's decision. Nothing
   *  here may throw. */
  onServerAnswered(validation: NoInfer<TValidation>): void;

  /** ask our server to confirm the change. Called only past the purchase-token
   *  guard, which is why it gets the narrowed response. */
  validateAsync(
    response: ConfirmedStorePurchaseResponse,
    accountId: string | undefined,
  ): Promise<TValidation>;

  /** turn a server refusal into this path's failure answer — in practice
   *  `buildStoreConfirmationFailure`, which decides whether the server settled the
   *  question or merely could not check. */
  buildValidationFailure(validation: NoInfer<TValidation>): StoreCrossGradeOutcome;

  /** whether the store queued the change for the end of the billing cycle rather
   *  than applying it now. Only the Apple paths supply it (see
   *  `isAppleCrossGradeScheduled`); when absent, the success answer carries no
   *  `scheduled` field at all. */
  isScheduled?(validation: NoInfer<TValidation>): boolean;

  /** refresh the session so the app picks up the change. Runs past the token
   *  guard, so a throw here is answered with the flag — the personal paths pass a
   *  refresh that swallows its own errors, the coach paths one that does not. */
  finishAsync(): Promise<unknown>;
}

/** Apple defers a downgrade to the end of the billing cycle. After validate, the
 *  server reports the active product in `productId` and the queued one in
 *  `autoRenewProductId`: the change is queued iff the renewal preference moved to the
 *  requested product while the active product did not. Using the server's fields
 *  instead of a client-side guess avoids false positives on a sandbox short-circuit,
 *  where the entitlement state alone looks the same as a real downgrade. */
export function isAppleCrossGradeScheduled(
  validation: { productId?: string; autoRenewProductId?: string },
  requestedProductId: string,
): boolean {
  return validation.autoRenewProductId === requestedProductId &&
    validation.productId !== requestedProductId;
}

/**
 * Run one store cross-grade and answer for it.
 *
 * Throws only what was thrown before the store handed over a purchase token; every
 * later failure comes back as an outcome.
 */
export async function runStoreCrossGradeAsync<TValidation extends StoreValidationResponse>(
  flow: StoreCrossGradeFlow<TValidation>,
): Promise<StoreCrossGradeOutcome> {
  // becomes true the moment the store hands us a purchase token — see the rule in
  // this file's header comment.
  let storeChargedTheBuyer = false;

  try {
    const accountId = flow.getAccountIdAsync ? await flow.getAccountIdAsync() : undefined;
    const response = await flow.runStoreCallAsync(accountId);

    if (response.canceled) {
      if (response.staleTransaction && flow.reportStaleTransaction) {
        flow.reportStaleTransaction(response);
      }
      return { success: false, canceled: true };
    }

    // defensive guard — the plugin declares purchaseToken optional, and an empty
    // token must never reach a validate endpoint.
    const purchaseToken = response.purchaseToken;
    if (!purchaseToken) {
      return { success: false, error: NO_PURCHASE_TOKEN_ERROR };
    }

    storeChargedTheBuyer = true;

    // the device could not check the receipt's signature. Not a failure: our
    // server confirms the change with the store itself.
    if (response.unverifiedOnDevice) {
      flow.reportUnverifiedOnDevice(response);
    }

    // write it down before asking our server anything. If the call below throws,
    // this record is the only thing that knows the change happened (#5611).
    const confirmedResponse = { ...response, purchaseToken };
    flow.rememberUnconfirmedPurchase(confirmedResponse, accountId);

    const validation = await flow.validateAsync(confirmedResponse, accountId);
    // our server answered — the record above has done its job either way.
    flow.onServerAnswered(validation);
    if (validation.error) {
      return flow.buildValidationFailure(validation);
    }

    if (flow.isScheduled) {
      const scheduled = flow.isScheduled(validation);
      await flow.finishAsync();
      return { success: true, scheduled };
    }
    await flow.finishAsync();
    return { success: true };
  } catch (error) {
    // no token yet: no evidence the change happened, and the caller does more with
    // the error than we could. Let it through untouched.
    if (!storeChargedTheBuyer) throw error;
    return {
      success: false,
      error: describeError(error),
      confirmationFailedAfterPurchase: true,
    };
  }
}
